// Data sources abstract where the map JSON lives. The default HTTP source
// fetches a manifest plus per-building files from a static folder; hosts can
// supply their own implementation (e.g. UMapsTools passes an import.meta.glob
// adapter) as long as it exposes the same three async methods.
//
// Optional: `imageUrl(relPath)` resolves a stored relative image path (e.g. an
// intersection node's panorama) to a loadable URL. If absent, the raw path is
// used as-is.
//
// Optional: `loadPlacePhotos()` → `{ [placeId]: [url, …] }` — extra photos of a
// place that live outside the snapshot, e.g. student submissions a host has
// accepted. Merged onto each place's own `images`. The HTTP source below does
// not implement it (there is no static file for something that changes daily);
// a host wraps this source and adds it. See PathfinderPage.js in UMapsWeb.

/**
 * HTTP data source for a static folder laid out as:
 *   <baseUrl>/manifest.json          → { buildings: [{ slug, name? }] }
 *   <baseUrl>/<slug>.json            → structure polygons (optional per building)
 *   <baseUrl>/nav_<slug>.json        → nav graph (optional per building)
 *   <baseUrl>/room_info.json         → { rooms: [{ building, roomNumber, capacity, roomType }] } (optional)
 *   <baseUrl>/parking_lots.json      → [{ lot, sections: [{ name, polygon: [{latitude,longitude}] }] }] (optional)
 *   <baseUrl>/places.json            → [{ id, type, name, building, roomNumber, floor, lat, lng, images, … }] (optional)
 *   <baseUrl>/RoomNames/<slug>.csv   → per-building friendly room names (optional; cols: Room Number, Room Name, Room Type)
 */
export function createHttpDataSource(baseUrl, { imagesBase, picsBase } = {}) {
  const root = String(baseUrl).replace(/\/?$/, '/');
  // Image folders sit BESIDE the snapshot, not inside it: the snapshot is JSON
  // that gets regenerated wholesale, while photos are large and land separately.
  const parent = root.replace(/[^/]+\/$/, '');
  const imgRoot = String(imagesBase ?? parent + 'buildingfiles/').replace(/\/?$/, '/');
  const picRoot = String(picsBase ?? parent + 'UofMPics/').replace(/\/?$/, '/');
  const getJson = async (path, { optional = false } = {}) => {
    const url = root + path;
    const res = await fetch(url);
    if (!res.ok) {
      if (optional) return null;
      throw new Error(`Failed to load ${url}: ${res.status}`);
    }
    // SPA-hosted static folders (e.g. a Vercel/CRA rewrite) answer a MISSING
    // file with index.html + 200 instead of a 404. The pseudo-buildings
    // (outdoors, building_connections, tunnels) have only a nav_<slug>.json and
    // no structure <slug>.json, so their optional loadBuilding() hit exactly
    // this: an HTML body that then blew up res.json() ("The string did not match
    // the expected pattern." in Safari / "Unexpected token '<'" in Chrome).
    // Treat an HTML response as "not there": null for optional, clear error otherwise.
    const ctype = (res.headers.get('content-type') || '').toLowerCase();
    if (ctype.includes('text/html')) {
      if (optional) return null;
      throw new Error(`Expected JSON at ${url} but got an HTML page (file missing?).`);
    }
    return res.json();
  };
  // Fetch a static text file (a RoomNames CSV). Missing files answer with a 404
  // or, on SPA hosts, an index.html page — both mean "not there" → null.
  const getText = async (path) => {
    const url = root + path;
    let res;
    try { res = await fetch(url); } catch { return null; }
    if (!res.ok) return null;
    const ctype = (res.headers.get('content-type') || '').toLowerCase();
    if (ctype.includes('text/html')) return null;
    return res.text();
  };
  const encodePath = (rel) => String(rel).split('/').map(encodeURIComponent).join('/');

  return {
    /**
     * A stored image path → a servable URL. Three cases, mirroring how the
     * authoring tool stores them:
     *  · an absolute URL (a Vercel Blob upload) passes straight through;
     *  · "<slug>/images/{places,pathfinding}/<file>" is a photo filed beside its
     *    building — a place picture or an intersection panorama;
     *  · anything else is a path in the older shared UofMPics library.
     *
     * `variant` asks for a pre-built size instead of the original: 'thumb' for
     * the 62x50 list icon, 'card' for the detail strip, 'full' for the
     * lightbox, 'pano' for the panorama window. scripts/optimize-pathfinder-
     * images.js builds these beside the source, named by inserting the tier
     * before a .webp extension, so the JSON keeps storing one plain path and
     * the caller picks the size it is actually about to paint.
     *
     * Only building-filed photos have variants. An absolute URL is somebody
     * else's file and the shared library was never processed, so both ignore
     * the argument and hand back the original — callers can pass a variant
     * unconditionally.
     */
    imageUrl: (rel, variant) => {
      if (!rel) return '';
      if (/^(https?:|data:|blob:)/i.test(rel)) return rel;
      const m = String(rel).match(/^([a-z0-9_-]+)\/images\/(places|pathfinding)\/(.+)$/i);
      if (!m) return picRoot + encodePath(rel);
      const want = variant ? String(rel).replace(/\.[^./]+$/, '') + `.${variant}.webp` : rel;
      return imgRoot + encodePath(want);
    },
    listBuildings: async () => (await getJson('manifest.json')).buildings || [],
    /**
     * Printed wayfinding signs: `?qr=<code>` opens the app already standing at
     * that sign's nav node.
     *
     * Optional, and a missing file must stay harmless — every scanned sign then
     * falls back to the ordinary search screen rather than to a dead end. The
     * file is authored in UMapsTools and copied into the snapshot by
     * scripts/sync-to-umapsweb.py; without that copy every sign on campus lands
     * on the fallback, which is the failure mode to watch for.
     */
    loadQrCodes: () => getJson('qr_codes.json', { optional: true }),

    // Every building's footprint in one file. Optional: without it the viewer
    // falls back to fetching each building whole, which is what it used to do.
    loadOutlines: () => getJson('outlines.json', { optional: true }),
    loadBuilding: (slug) => getJson(`${encodeURIComponent(slug)}.json`, { optional: true }),
    loadNav: (slug) => getJson(`nav_${encodeURIComponent(slug)}.json`, { optional: true }),
    loadRoomInfo: () => getJson('room_info.json', { optional: true }),
    loadParking: () => getJson('parking_lots.json', { optional: true }),
    // Study spots, libraries, restaurants… — what the "Study spots" panel lists.
    loadPlaces: () => getJson('places.json', { optional: true }),

    /**
     * The campus bus stops: what Winnipeg Transit says is there, plus the nav
     * node each is walked from. Authored in UMapsTools' Commuting editor and
     * copied into the snapshot by scripts/sync-to-umapsweb.py.
     *
     * Optional, and a missing file must stay harmless — the Bus stops tab
     * simply never appears, exactly as an amenity category with no places does.
     */
    loadTransitStops: () => getJson('transit_stops.json', { optional: true }),

    /**
     * The term's timetable, for Course Mode: `{ termName, rooms, sections }`,
     * built by scripts/build-courses.js from UMapsTools' Aurora export.
     *
     * NOT loaded with the rest of the snapshot — it is ~100 KB gzipped and says
     * nothing to a visitor who never turns the toggle on, so the caller fetches
     * it on that tap. Optional and non-fatal: a host without the file answers
     * null, and Course Mode says the timetable is unavailable rather than
     * failing — the toggle is drawn before anything is known about the file.
     */
    loadCourses: () => getJson('courses.json', { optional: true }),

    /**
     * Live-ish departures for one stop: `{ stop, routes, departures, exhausted,
     * searchedUntil }`.
     *
     * NOT part of the snapshot — it is the one thing here that changes by the
     * minute, and it needs an API key that must never reach the browser. So it
     * goes through the host's own server (/api/transit), which is also why it is
     * a dataSource method rather than a fetch inside the panel: a host without
     * that endpoint simply does not supply this, and the panel says bus times
     * are unavailable instead of failing.
     */
    loadDepartures: async (stopKey) => {
      const res = await fetch(`/api/transit?stop=${encodeURIComponent(stopKey)}`);
      const ctype = (res.headers.get('content-type') || '').toLowerCase();
      // An SPA host answers an unmounted endpoint with index.html and a 200,
      // which would otherwise arrive as a JSON parse error.
      if (!ctype.includes('application/json')) {
        throw new Error('Bus times are unavailable right now.');
      }
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Bus times are unavailable right now.');
      return body;
    },
    // Friendly room names, hand-entered per building in RoomNames/<slug>.csv
    // (cols: Room Number, Room Name, Room Type). Editable in place — drop or
    // change a CSV and it's picked up on the next load, no rebuild. Fetched per
    // manifest slug; buildings without a sheet just answer "not there".
    loadRoomNames: async () => {
      let list;
      try { list = (await getJson('manifest.json')).buildings || []; }
      catch { return null; }
      const rows = [];
      await Promise.all(list.map(async ({ slug }) => {
        const text = await getText(`RoomNames/${encodeURIComponent(slug)}.csv`);
        if (text) rows.push(...parseRoomNamesCsv(text, slug));
      }));
      return rows.length ? rows : null;
    },
  };
}

/**
 * Parse a RoomNames CSV (cols: Room Number, Room Name, Room Type) into
 * `[{ building, roomNumber, roomName }]`, keyed to the app slug. Rows with a
 * blank name are skipped (nothing to show). Handles double-quoted fields so a
 * name can contain a comma.
 */
export function parseRoomNamesCsv(text, slug) {
  const parseLine = (line) => {
    const out = []; let cur = '', q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) {
        if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
        else cur += c;
      } else if (c === '"') q = true;
      else if (c === ',') { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur);
    return out;
  };
  const rows = [];
  const lines = String(text).split(/\r?\n/).filter(l => l.trim());
  for (let i = 1; i < lines.length; i++) { // skip header row
    const [roomNumber, roomName] = parseLine(lines[i]);
    if (roomNumber && roomName && roomName.trim()) {
      rows.push({ building: slug, roomNumber: roomNumber.trim(), roomName: roomName.trim() });
    }
  }
  return rows;
}

/** Loads every building's files through a data source, ready for buildMapData(). */
export async function loadAllMapFiles(dataSource) {
  const list = await dataSource.listBuildings();
  // Footprints only. The insides of a building are ~40x the bytes of its
  // outline and become thousands of map objects, and none of it says anything
  // until the user routes through that building — so it waits for
  // loadBuildingInterior(). One bundled request replaces ~38 individual ones.
  let outlines = null;
  if (dataSource.loadOutlines) {
    try { outlines = await dataSource.loadOutlines(); } catch { outlines = null; }
  }
  const byslug = new Map((outlines?.buildings || []).map(b => [b.slug, b]));

  return Promise.all(list.map(async ({ slug, name }) => {
    const stub = byslug.get(slug);
    return {
      slug,
      name,
      // No bundle (a host with its own dataSource, or the file not built yet):
      // fall back to the whole building so nothing silently loses its polygons.
      building: stub
        ? { building: stub.name, polygons: stub.polygons, floors: stub.floors, outlineOnly: true }
        : await dataSource.loadBuilding(slug),
      nav: await dataSource.loadNav(slug),
    };
  }));
}
